/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/*
 * The file is automatically generated by Serenity UI.
 *
 * ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
 *
 * Found an issue? Submit a task to the ClickUp board:
 * https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
 */
import * as stylex from '@stylexjs/stylex';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { type ForwardedRef, type ReactNode, createContext, forwardRef, useMemo } from 'react';
import { type ContextValue, Provider, useContextProps } from 'react-aria-components';
import { createPortal } from 'react-dom';
import { $ } from '../../tokens.stylex';

import type { ScaleLinear } from '@visx/vendor/d3-scale';
import { ChartLegendContext } from '../chart-legend/chart-legend.component';

interface ChartCrosshairProps<T = unknown> {
  overlayContainer?: HTMLDivElement;
  lineDasharray?: string;
  data?: T[] | T[][];
  series?: {
    label?: string;
    color: string;
  }[];
  innerWidth?: number;
  innerHeight?: number;
  margin?: { top: number; right: number; bottom: number; left: number };
  xScale?: ScaleLinear<number, number, never>;
  yScale?: ScaleLinear<number, number, never>;
  xAccessor?: (datum: T) => number;
  yAccessor?: (datum: T) => number;
  children?: (context: {
    series: {
      label?: string;
      color: string;
      value?: number;
    }[];
    x: number;
  }) => ReactNode;
  style?: stylex.StyleXStyles;
}

const ChartCrosshairContext = createContext<ContextValue<ChartCrosshairProps, HTMLDivElement>>({});

const ChartCrosshair = forwardRef((props: ChartCrosshairProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, ChartCrosshairContext);

  const {
    margin,
    xScale,
    yScale,
    data,
    series,
    overlayContainer,
    xAccessor,
    yAccessor,
    innerWidth,
    innerHeight,
    style,
    children,
    ...coreProps
  } = props;

  const { tooltipData, tooltipLeft, tooltipTop, showTooltip, hideTooltip } = useTooltip<
    { x: number; y: number } | { x: number; y: number[] }
  >();

  const seriesWithData = useMemo(() => {
    if (!tooltipData || !series || series.length === 0) {
      return [];
    }

    const yValues = Array.isArray(tooltipData.y) ? tooltipData.y : [tooltipData.y];

    return series.map((item, index) => ({
      ...item,
      value: yValues[index],
    }));
  }, [series, tooltipData]);

  if (!overlayContainer) {
    return null;
  }

  const hasTooltip = tooltipLeft != null && tooltipTop != null;
  const x = hasTooltip ? tooltipLeft - margin!.left : 0;

  return (
    <>
      {createPortal(
        <div {...stylex.props(styles.underlay)}>
          <svg
            width={innerWidth}
            height={innerHeight}
            {...stylex.props(styles.underlaySvg({ left: margin!.left, top: margin!.top }))}
          >
            {hasTooltip && (
              <g {...stylex.props(styles.crosshair)}>
                <g {...stylex.props(styles.axis(x))}>
                  <line x1={0} x2={0} y1={0} y2={innerHeight} {...stylex.props(lineStyles.base, style)} />
                </g>
              </g>
            )}
          </svg>
        </div>,
        overlayContainer,
      )}
      {createPortal(
        <div ref={ref} {...stylex.props(styles.overlay)} {...coreProps}>
          <svg
            width={innerWidth}
            height={innerHeight}
            {...stylex.props(styles.overlaySvg({ left: margin!.left, top: margin!.top }))}
          >
            <rect
              width={innerWidth}
              height={innerHeight}
              fill="transparent"
              onMouseMove={(event) => {
                const point = localPoint(event);
                if (!point) {
                  return;
                }

                const xValue = xScale!.invert(point.x);
                const closestPoints = getClosestPoints(data!, xValue, xAccessor!, yAccessor!);
                if (!closestPoints) {
                  return;
                }

                showTooltip({
                  tooltipData: closestPoints,
                  tooltipLeft: margin!.left + xScale!(closestPoints.x),
                  tooltipTop: margin!.top + yScale!(closestPoints.y[0]),
                });
              }}
              onMouseLeave={hideTooltip}
            />
          </svg>
          <Provider
            values={[
              [
                ChartLegendContext,
                {
                  tooltipData,
                  tooltipLeft,
                  tooltipTop,
                  portalContainer: overlayContainer,
                },
              ],
            ]}
          >
            {!!tooltipData && children?.({ series: seriesWithData, x: tooltipData.x })}
          </Provider>
        </div>,
        overlayContainer,
      )}
    </>
  );
});

const styles = stylex.create({
  overlay: {
    inset: 0,
    pointerEvents: 'auto',
    position: 'absolute',
    zIndex: 2,
  },
  underlay: {
    inset: 0,
    pointerEvents: 'none',
    position: 'absolute',
    zIndex: 0,
  },
  crosshair: {
    pointerEvents: 'none',
  },
  underlaySvg: (offset: { left: number; top: number }) => ({
    pointerEvents: 'none',
    position: 'absolute',
    left: offset.left,
    top: offset.top,
  }),
  overlaySvg: (offset: { left: number; top: number }) => ({
    pointerEvents: 'auto',
    position: 'absolute',
    left: offset.left,
    top: offset.top,
  }),
  axis: (x: number) => ({
    transform: `translateX(${x}px)`,
    transformBox: 'fill-box',
    transformOrigin: '0 0',
    transitionDuration: '180ms',
    transitionProperty: 'transform',
    transitionTimingFunction: 'ease-out',
    willChange: 'transform',
  }),
});

const lineStyles = stylex.create({
  base: {
    stroke: $.iconDisabled,
    strokeDasharray: '6 4',
    strokeWidth: 1,
  },
});

const getClosestPoints = <T,>(
  data: T[] | T[][],
  xValue: number,
  xAccessor: (datum: T) => number,
  yAccessor: (datum: T) => number,
) => {
  const series = ((Array.isArray(data[0]) ? data : [data]) as T[][]).filter((items) => items.length > 0);

  if (series.length === 0) {
    return null;
  }

  const closestBySeries = series.map((items) =>
    items.reduce((acc, datum) => {
      return Math.abs(xAccessor(datum) - xValue) < Math.abs(xAccessor(acc) - xValue) ? datum : acc;
    }, items[0]),
  );

  const overallClosest = closestBySeries.reduce((acc, datum) => {
    return Math.abs(xAccessor(datum) - xValue) < Math.abs(xAccessor(acc) - xValue) ? datum : acc;
  }, closestBySeries[0]);

  return {
    x: xAccessor(overallClosest),
    y: closestBySeries.map((datum) => yAccessor(datum)),
  };
};

export { ChartCrosshair, ChartCrosshairContext, type ChartCrosshairProps };
